import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Card,
  Typography,
  Popconfirm,
  message,
  Row,
  Col,
  Dropdown,
  Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ExportOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService } from '../../api/users';
import { User, UserRole } from '../../types';
import { useAuth } from '../../hooks/useAuth';

const { Title } = Typography;
const { Search } = Input;

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  
  const [searchText, setSearchText] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | undefined>();
  const [cursor, setCursor] = useState<string | undefined>();
  const [limit] = useState(10);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  // Сброс при изменении фильтров
  useEffect(() => {
    setCursor(undefined);
    setAllUsers([]);
  }, [searchText, selectedRole]);

  // Сброс локального состояния при монтировании
  useEffect(() => {
    // При монтировании сбрасываем состояние для свежих данных
    setCursor(undefined);
    setAllUsers([]);
    setHasNextPage(false);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['users', cursor, limit, searchText, selectedRole],
    queryFn: () => usersService.getUsers(
      cursor,
      limit,
      searchText,
      selectedRole
    ),
    onSuccess: (newData) => {
      if (!cursor) {
        // Первая загрузка - заменяем все данные
        setAllUsers(newData.items || []);
      } else {
        // Загрузка следующей страницы - добавляем к существующим
        setAllUsers(prev => [...prev, ...(newData.items || [])]);
      }
      setHasNextPage(newData.hasNextPage || false);
    },
    // Перезагружать при каждом возврате на страницу
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const deleteMutation = useMutation({
    mutationFn: usersService.deleteUser,
    onSuccess: () => {
      message.success(t('messages.userDeleted'));
      // Сбрасываем локальное состояние и перезагружаем
      setCursor(undefined);
      setAllUsers([]);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorDeletingUser'));
    },
  });

  const activateUserMutation = useMutation({
    mutationFn: (id: string) => usersService.activateUser(id),
    onSuccess: () => {
      message.success(t('messages.userActivated'));
      setCursor(undefined);
      setAllUsers([]);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorActivating'));
    },
  });

  const deactivateUserMutation = useMutation({
    mutationFn: (id: string) => usersService.deactivateUser(id),
    onSuccess: () => {
      message.success(t('messages.userDeactivated'));
      setCursor(undefined);
      setAllUsers([]);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: Error) => {
      message.error(error?.message || t('messages.errorDeactivating'));
    },
  });

  const getRoleColor = (role: UserRole) => {
    const colors = {
      SUPERADMIN: 'red',
      ADMIN: 'orange', 
      OPERATOR: 'blue',
      TRANSLATOR: 'green',
      SUPPORT: 'cyan',
      READONLY: 'gray',
    };
    return colors[role] || 'default';
  };

  const getRoleDisplayName = (role: UserRole) => {
    return t(`userRoles.${role}`, role);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleToggleStatus = (id: string, isActive: boolean) => {
    if (isActive) {
      deactivateUserMutation.mutate(id);
    } else {
      activateUserMutation.mutate(id);
    }
  };

  const handleLoadMore = () => {
    if (data?.nextCursor && hasNextPage) {
      setCursor(data.nextCursor);
    }
  };

  // Функция refresh доступна через handleRefresh
  // const handleRefresh = () => {
  //   setCursor(undefined);
  //   setAllUsers([]);
  //   refetch();
  // };

  const handleExport = async () => {
    try {
      const blob = await usersService.exportUsers('csv');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Ошибка при экспорте данных');
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('users.email'),
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: t('users.firstName'),
      key: 'fullName',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      sorter: true,
    },
    {
      title: t('users.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <Tag color={getRoleColor(role)}>
          {getRoleDisplayName(role)}
        </Tag>
      ),
      filters: [
        { text: t('userRoles.SUPERADMIN'), value: 'SUPERADMIN' },
        { text: t('userRoles.ADMIN'), value: 'ADMIN' },
        { text: t('userRoles.OPERATOR'), value: 'OPERATOR' },
        { text: t('userRoles.TRANSLATOR'), value: 'TRANSLATOR' },
        { text: t('userRoles.SUPPORT'), value: 'SUPPORT' },
        { text: t('userRoles.READONLY'), value: 'READONLY' },
      ],
    },
    {
      title: t('users.created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
      sorter: true,
    },
    {
      title: t('users.actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: t('common.edit'),
            onClick: () => navigate(`/users/${record.id}/edit`),
          },
          {
            key: 'toggle-status',
            label: record.isActive ? t('users.deactivate') : t('users.activate'),
            onClick: () => handleToggleStatus(record.id, true), // This would be dynamic
          },
          ...(hasRole(['SUPERADMIN']) ? [{
            key: 'delete',
            icon: <DeleteOutlined />,
            label: t('common.delete'),
            danger: true,
            onClick: () => handleDelete(record.id),
          }] : []),
        ];

        return (
          <Space size="middle">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/users/${record.id}/edit`)}
            />
            {hasRole(['SUPERADMIN']) && (
              <Popconfirm
                title={t('confirmations.deleteUser')}
                description={t('confirmations.deleteTitle')}
                onConfirm={() => handleDelete(record.id)}
                okText={t('common.yes')}
                cancelText={t('common.no')}
              >
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  danger
                />
              </Popconfirm>
            )}
            <Dropdown menu={{ items: menuItems }}>
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>{t('users.title')}</Title>
        </Col>
        <Col>
          {hasRole(['SUPERADMIN', 'ADMIN']) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/users/create')}
            >
              {t('users.createUser')}
            </Button>
          )}
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Search
              placeholder={t('users.searchPlaceholder')}
              allowClear
              onSearch={setSearchText}
              style={{ width: '100%' }}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder={t('users.filterByRole')}
              allowClear
              style={{ width: '100%' }}
              onChange={setSelectedRole}
              options={[
                { label: t('userRoles.SUPERADMIN'), value: 'SUPERADMIN' },
                { label: t('userRoles.ADMIN'), value: 'ADMIN' },
                { label: t('userRoles.OPERATOR'), value: 'OPERATOR' },
                { label: t('userRoles.TRANSLATOR'), value: 'TRANSLATOR' },
                { label: t('userRoles.SUPPORT'), value: 'SUPPORT' },
                { label: t('userRoles.READONLY'), value: 'READONLY' },
              ]}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              style={{ width: '100%' }}
            >
              {t('users.exportCsv')}
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={allUsers}
          loading={isLoading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 800 }}
        />
        
        {hasNextPage && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button 
              onClick={handleLoadMore}
              loading={isLoading}
              type="primary"
              ghost
            >
              {t('users.loadMore')} ({allUsers.length} {t('users.loaded')})
            </Button>
          </div>
        )}
        
        {!isLoading && allUsers.length === 0 && (
          <Empty 
            description={t('users.noUsersFound')} 
            style={{ margin: '40px 0' }}
          />
        )}
      </Card>
    </div>
  );
};

export default UsersList;
